// src/components/emision/FoliosPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, Typography, Chip, CircularProgress,
  Tab, Tabs, Table, TableHead, TableBody, TableRow, TableCell,
  Alert, Tooltip, IconButton,
} from '@mui/material';
import { DeleteOutline, RefreshOutlined } from '@mui/icons-material';
import { emisionDTEService, TIPOS_DTE } from '../../services/emisionDTEService';

const ESTADO_COLOR = {
  recibido:   'success',
  fallido:    'error',
  disponible: 'default',
};

const ESTADO_LABEL = {
  recibido:   'Recibido SII',
  fallido:    'Fallido',
  disponible: 'Disponible',
};

export default function FoliosPanel({ open, onClose, rutEmpresa, cafsTipos }) {
  const [tabIdx, setTabIdx] = useState(0);
  const [detalles, setDetalles] = useState({});  // tipo_dte → response
  const [loading, setLoading] = useState({});
  const [liberando, setLiberando] = useState({});
  const [error, setError] = useState(null);

  const tipoDteActual = cafsTipos[tabIdx]?.tipo_dte;

  const cargar = useCallback(async (tipoDte) => {
    if (!tipoDte || !rutEmpresa) return;
    setLoading(prev => ({ ...prev, [tipoDte]: true }));
    try {
      const data = await emisionDTEService.detalleFolios(rutEmpresa, tipoDte);
      setDetalles(prev => ({ ...prev, [tipoDte]: data }));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(prev => ({ ...prev, [tipoDte]: false }));
    }
  }, [rutEmpresa]);

  useEffect(() => {
    if (open && tipoDteActual) cargar(tipoDteActual);
  }, [open, tipoDteActual, cargar]);

  const liberar = async (folio, { recargar = true } = {}) => {
    const key = `${tipoDteActual}-${folio}`;
    setLiberando(prev => ({ ...prev, [key]: true }));
    try {
      await emisionDTEService.liberarFolio(rutEmpresa, tipoDteActual, folio);
      if (recargar) await cargar(tipoDteActual);
    } catch (e) {
      setError(e.message);
    } finally {
      setLiberando(prev => ({ ...prev, [key]: false }));
    }
  };

  const liberarTodosFallidos = async () => {
    const detalle = detalles[tipoDteActual];
    if (!detalle) return;
    const fallidos = detalle.folios.filter(f => f.estado === 'fallido');
    for (const f of fallidos) {
      await liberar(f.folio, { recargar: false });
    }
    await cargar(tipoDteActual);
  };

  const detalle = detalles[tipoDteActual];
  const isLoading = loading[tipoDteActual];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Gestión de Folios</Typography>
          {detalle && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip label={`${detalle.resumen.recibidos} recibidos`} size="small" color="success" variant="outlined" />
              <Chip label={`${detalle.resumen.fallidos} fallidos`} size="small" color="error" variant="outlined" />
              <Chip label={`${detalle.resumen.disponibles} disponibles`} size="small" variant="outlined" />
            </Box>
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {cafsTipos.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="info">No hay CAFs cargados para esta empresa.</Alert>
          </Box>
        ) : (
          <>
            <Tabs
              value={tabIdx}
              onChange={(_, v) => setTabIdx(v)}
              variant="scrollable"
              sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
            >
              {cafsTipos.map(c => (
                <Tab
                  key={c.tipo_dte}
                  label={`T${c.tipo_dte}`}
                  sx={{ fontSize: '0.8rem', minWidth: 60 }}
                />
              ))}
            </Tabs>

            <Box sx={{ p: 2 }}>
              {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : !detalle ? (
                <Alert severity="info">Cargando...</Alert>
              ) : (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {detalle.nombre} · Ambiente: <strong>{detalle.ambiente}</strong> · Rango: {detalle.folio_desde}–{detalle.folio_hasta}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {detalle.resumen.fallidos > 0 && (
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={liberarTodosFallidos}
                          sx={{ fontSize: '0.72rem' }}
                        >
                          Liberar todos los fallidos ({detalle.resumen.fallidos})
                        </Button>
                      )}
                      <Tooltip title="Recargar">
                        <IconButton size="small" onClick={() => cargar(tipoDteActual)}>
                          <RefreshOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Folio</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell>TrackID</TableCell>
                        <TableCell>Fecha</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detalle.folios.map(f => {
                        const key = `${tipoDteActual}-${f.folio}`;
                        return (
                          <TableRow key={f.folio} sx={{ opacity: f.estado === 'disponible' ? 0.55 : 1 }}>
                            <TableCell>
                              <Typography variant="body2" fontFamily="monospace">{f.folio}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={ESTADO_LABEL[f.estado]}
                                size="small"
                                color={ESTADO_COLOR[f.estado]}
                                variant={f.estado === 'disponible' ? 'outlined' : 'filled'}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" fontFamily="monospace">
                                {f.trackid || '–'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">{f.fecha_emision || '–'}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              {f.estado === 'fallido' && (
                                <Tooltip title="Liberar folio (eliminar registro fallido)">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      disabled={!!liberando[key]}
                                      onClick={() => liberar(f.folio)}
                                    >
                                      {liberando[key]
                                        ? <CircularProgress size={14} />
                                        : <DeleteOutline fontSize="small" />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
